<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto focus:outline-none" tabindex="0">
        <form @submit.prevent="saveFile">
            <div class="max-w-6xl mx-auto sm:px-6 lg:px-8 px-5 pt-6 pb-2">
                <div class="flex items-center justify-between gap-4">
                    <div class="flex-1 min-w-0">
                        <h1 class="text-2xl font-semibold text-gray-900">Datei editieren</h1>
                    </div>
                    <div class="flex items-center">
                        <router-link class="btn-stage-secondary mr-3" :to="fileManagerListPath">
                            Abbrechen
                        </router-link>
                        <button v-if="canDeleteFile" class="btn-stage-danger mr-3" type="button" @click="deleteModal = true">
                            Löschen
                        </button>
                        <button v-if="canEditFileDetails" class="btn-stage-primary" type="submit">
                            Speichern
                        </button>
                    </div>
                </div>
            </div>

            <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 pb-6">
                <div class="mt-6 space-y-6">
                    <loading :status="loading"/>
                    <div class="mt-1 bg-white border border-bordergray-100 shadow sm:rounded overflow-hidden">
                        <div class="px-4 py-5 sm:p-6 md:grid md:grid-cols-3 md:gap-x-6 md:gap-y-5">
                            <div class="md:col-span-1">
                                <h3 class="text-lg font-medium leading-6 text-gray-900">{{ canEditFileDetails ? 'Datei' : 'Dateiname' }}</h3>
                                <p v-if="canEditFileDetails" class="mt-1 text-sm leading-5 text-gray-500">Dateiname prüfen oder ändern und optional eine Ersatzdatei hochladen.</p>
                            </div>
                            <div class="mt-5 md:mt-0 md:col-span-2">
                                <template v-if="canEditFileDetails">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="name">Dateiname</label>
                                    <div class="mt-1 rounded">
                                        <input id="name" v-model="file.name" class="form-input text-sm block w-full border-bordergray-100 rounded" :disabled="!canEditFileDetails" required>
                                    </div>
                                    <div class="mt-2 text-xs text-gray-500">
                                        Dateiname beim Upload am {{ uploadedAtDate }} um {{ uploadedAtTime }}: {{ file.original_name }}
                                    </div>
                                    <div class="mt-3 border border-bordergray-100 rounded p-3">
                                        <input ref="replaceInput" hidden type="file" :disabled="!canEditFileDetails" @change="onReplaceFileSelected($event)">
                                        <div class="flex items-center justify-between gap-3 w-full">
                                            <button class="btn-stage-secondary" type="button" :disabled="!canEditFileDetails" @click="selectReplaceFile">
                                                Datei ersetzen
                                            </button>
                                            <span v-if="replacementFileName" class="text-xs text-gray-500 truncate">
                                                Ausgewählt: {{ replacementFileName }}
                                            </span>
                                            <span v-else class="text-xs text-gray-500 truncate">
                                                Keine Ersatzdatei ausgewählt
                                            </span>
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="text-lg text-gray-900 break-all">{{ displayFileName }}</div>
                                    <div v-if="hasShortDescription" class="mt-4">
                                        <div class="text-sm font-medium text-gray-700">Kurzbeschreibung</div>
                                        <div class="mt-1 text-sm text-gray-600 whitespace-pre-line">{{ file.short_description }}</div>
                                    </div>
                                </template>
                            </div>

                            <div v-if="canEditFileDetails && canManageCategories" class="md:col-start-2 md:col-span-2">
                                <label class="block text-sm font-medium leading-5 text-gray-700" for="file-category">Kategorie</label>
                                <select
                                    id="file-category"
                                    v-model="file.category_id"
                                    :disabled="!canEditFileDetails"
                                    class="form-select text-sm mt-1 w-full border-bordergray-100 rounded"
                                >
                                    <option :value="null">Keine Kategorie</option>
                                    <option v-for="category in categories" :key="'edit-category-' + category.id" :value="Number(category.id)">
                                        {{ category.name }}
                                    </option>
                                </select>
                            </div>

                            <div v-if="canEditFileDetails" class="md:col-start-2 md:col-span-2">
                                <label class="block text-sm font-medium leading-5 text-gray-700" for="short_description">Kurzbeschreibung</label>
                                <textarea id="short_description" v-model="file.short_description" :disabled="!canEditFileDetails" class="form-textarea text-sm mt-1 w-full h-24 border-bordergray-100 rounded"></textarea>
                            </div>
                        </div>
                    </div>

                    <div v-if="showPermissionSection" class="bg-white border border-bordergray-100 shadow sm:rounded md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ permissionSectionTitle }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">{{ permissionSectionDescription }}</p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                                <template v-if="isCustomer">
                                    <h4 class="text-sm font-medium text-gray-700 mb-2">Datei teilen</h4>
                                    <div class="border border-bordergray-100 rounded max-h-56 overflow-y-auto">
                                        <label v-for="customer in customers" :key="'customer-share-' + customer.id" class="flex items-center justify-between px-3 py-2 border-b border-bordergray-100 last:border-b-0">
                                            <span class="flex items-center text-sm text-gray-800">
                                                <img
                                                    :src="getUserAvatar(customer)"
                                                    :alt="customer ? customer.name : 'Avatar'"
                                                    class="h-6 w-6 rounded-full object-cover mr-2"
                                                >
                                                {{ customer.name }}
                                            </span>
                                            <span class="inline-flex items-center text-sm">
                                                <span class="mr-2 text-gray-500">lesen</span>
                                                <input v-model="getCustomerPermissionState(customer.id).read" class="form-checkbox h-4 w-4 text-gray-600 mr-4" type="checkbox" @change="handlePermissionReadChange(getCustomerPermissionState(customer.id))">
                                                <span class="mr-2 text-gray-500">schreiben</span>
                                                <input v-model="getCustomerPermissionState(customer.id).write" :disabled="!getCustomerPermissionState(customer.id).read" class="form-checkbox h-4 w-4 text-gray-600" type="checkbox" @change="handlePermissionWriteChange(getCustomerPermissionState(customer.id))">
                                            </span>
                                        </label>
                                        <div v-if="!customers.length" class="px-3 py-2 text-sm text-gray-500">
                                            Keine weiteren Kunden Ihres Unternehmens verfügbar.
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                <h4 class="text-sm font-medium text-gray-700 mb-2">Interne Freigaben</h4>
                                <div class="border border-bordergray-100 rounded max-h-56 overflow-y-auto">
                                    <label v-for="employee in internalEmployees" :key="'emp-' + employee.id" class="flex items-center justify-between px-3 py-2 border-b border-bordergray-100 last:border-b-0">
                                        <span class="flex items-center text-sm text-gray-800">
                                            <img
                                                :src="getUserAvatar(employee)"
                                                :alt="employee ? employee.name : 'Avatar'"
                                                class="h-6 w-6 rounded-full object-cover mr-2"
                                            >
                                            {{ employee.name }}
                                        </span>
                                        <span class="inline-flex items-center text-sm">
                                            <span class="mr-2 text-gray-500">lesen</span>
                                            <input v-model="getEmployeePermissionState(employee.id).read" class="form-checkbox h-4 w-4 text-gray-600 mr-4" type="checkbox" @change="handlePermissionReadChange(getEmployeePermissionState(employee.id))">
                                            <span class="mr-2 text-gray-500">schreiben</span>
                                            <input v-model="getEmployeePermissionState(employee.id).write" :disabled="!getEmployeePermissionState(employee.id).read" class="form-checkbox h-4 w-4 text-gray-600" type="checkbox" @change="handlePermissionWriteChange(getEmployeePermissionState(employee.id))">
                                        </span>
                                    </label>
                                    <div v-if="!internalEmployees.length" class="px-3 py-2 text-sm text-gray-500">
                                        Keine internen Benutzer verfügbar.
                                    </div>
                                </div>
                                <template v-if="isAdmin">
                                    <h4 class="text-sm font-medium text-gray-700 mt-5 mb-2">Kundenfreigabe</h4>
                                <div class="border border-bordergray-100 rounded max-h-56 overflow-y-auto">
                                    <label v-for="customer in customers" :key="'cust-' + customer.id" class="flex items-center justify-between px-3 py-2 border-b border-bordergray-100 last:border-b-0">
                                        <span class="flex items-center text-sm text-gray-800">
                                            <img
                                                :src="getUserAvatar(customer)"
                                                :alt="customer ? customer.name : 'Avatar'"
                                                class="h-6 w-6 rounded-full object-cover mr-2"
                                            >
                                            {{ customer.name }}
                                        </span>
                                        <span class="inline-flex items-center text-sm">
                                            <span class="mr-2 text-gray-500">lesen</span>
                                            <input v-model="getCustomerPermissionState(customer.id).read" class="form-checkbox h-4 w-4 text-gray-600 mr-4" type="checkbox" @change="handlePermissionReadChange(getCustomerPermissionState(customer.id))">
                                            <span class="mr-2 text-gray-500">schreiben</span>
                                            <input v-model="getCustomerPermissionState(customer.id).write" :disabled="!getCustomerPermissionState(customer.id).read" class="form-checkbox h-4 w-4 text-gray-600" type="checkbox" @change="handlePermissionWriteChange(getCustomerPermissionState(customer.id))">
                                        </span>
                                    </label>
                                </div>
                                    <h4 class="text-sm font-medium text-gray-700 mt-5 mb-2">Gastfreigaben</h4>
                                    <div class="border border-bordergray-100 rounded max-h-56 overflow-y-auto">
                                        <label v-for="guest in guestEmployees" :key="'guest-' + guest.id" class="flex items-center justify-between px-3 py-2 border-b border-bordergray-100 last:border-b-0">
                                            <span class="flex items-center text-sm text-gray-800">
                                                <img
                                                    :src="getUserAvatar(guest)"
                                                    :alt="guest ? guest.name : 'Avatar'"
                                                    class="h-6 w-6 rounded-full object-cover mr-2"
                                                >
                                                {{ guest.name }}
                                            </span>
                                            <span class="inline-flex items-center text-sm">
                                                <span class="mr-2 text-gray-500">lesen</span>
                                                <input v-model="getGuestPermissionState(guest.id).read" class="form-checkbox h-4 w-4 text-gray-600" type="checkbox" @change="handleGuestPermissionReadChange(getGuestPermissionState(guest.id))">
                                            </span>
                                        </label>
                                        <div v-if="!guestEmployees.length" class="px-3 py-2 text-sm text-gray-500">
                                            Keine Gastbenutzer verfügbar.
                                        </div>
                                    </div>
                                </template>
                                </template>
                            </div>
                        </div>
                </div>
            </div>
        </form>

        <confirm-dialog :open="deleteModal" title="Datei löschen" text="Soll die Datei wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden." confirm-label="Löschen" confirm-variant="danger" @close="deleteModal = false" @confirm="deleteFile" />
    </main>
</template>

<script>
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';
import {resolveAccessState} from "@/utils/access-state";
import {resolveReturnToPath} from "@/utils/list-route-state";
import {
    canManageFileCategories,
    canShowFilePermissionSection,
    filePermissionSectionDescription,
    filePermissionSectionTitle,
} from "@/utils/sharing-access";

export default {
    components: {ConfirmDialog},
    name: "file-manager-edit",
    metaInfo() {
        return {
            title: "Datei editieren"
        }
    },
    data() {
        return {
            loading: true,
            deleteModal: false,
            file: {
                name: '',
                original_name: '',
                short_description: '',
                category_id: null,
                employee_permissions: [],
                customer_permissions: [],
                can_write: false,
                can_manage_permissions: false,
                can_delete: false,
            },
            categories: [],
            employees: [],
            guests: [],
            customers: [],
            employeeMap: {},
            customerMap: {},
            replacementFile: null,
            replacementFileName: '',
        }
    },
    computed: {
        currentUser() {
            return this.$store && this.$store.state ? this.$store.state.user : null;
        },
        accessState() {
            return resolveAccessState(this.$store);
        },
        isCustomerFileManagerRoute() {
            return String(this.$route.path || '').startsWith('/file-manager');
        },
        fileManagerListPath() {
            const fallbackPath = this.isCustomerFileManagerRoute ? '/file-manager/list' : '/workspace/file-manager';
            return resolveReturnToPath(this.$route.query || {}, fallbackPath);
        },
        isAdmin() {
            return this.accessState.isAdmin;
        },
        isCustomer() {
            return this.accessState.isCustomer;
        },
        canWriteFile() {
            return !!(this.file && this.file.can_write);
        },
        canManageFilePermissions() {
            return !!(this.file && this.file.can_manage_permissions);
        },
        canDeleteFile() {
            return !!(this.file && this.file.can_delete);
        },
        canEditFileDetails() {
            return this.isAdmin || this.canWriteFile;
        },
        internalEmployees() {
            return this.employees;
        },
        guestEmployees() {
            return this.guests;
        },
        canManageCategories() {
            return canManageFileCategories(this.accessState, this.currentUser);
        },
        showPermissionSection() {
            return canShowFilePermissionSection(this.accessState, this.canManageFilePermissions);
        },
        permissionSectionTitle() {
            return filePermissionSectionTitle(this.accessState);
        },
        permissionSectionDescription() {
            return filePermissionSectionDescription(this.accessState);
        },
        uploadedAtDate() {
            if (!this.file || !this.file.created_at) {
                return '--.--.----';
            }
            return moment(this.file.created_at).locale(window.app.app_date_locale).format('DD.MM.YYYY');
        },
        uploadedAtTime() {
            if (!this.file || !this.file.created_at) {
                return '--:--';
            }
            return moment(this.file.created_at).locale(window.app.app_date_locale).format('HH:mm');
        },
        displayFileName() {
            return this.file.name || this.file.original_name || '-';
        },
        hasShortDescription() {
            return !!(this.file.short_description && String(this.file.short_description).trim() !== '');
        },
    },
    mounted() {
        this.fetchOptions();
        this.fetchFile();
    },
    watch: {
        '$route.params.uuid': function (newValue, oldValue) {
            if (!newValue || newValue === oldValue) {
                return;
            }

            this.resetFileState();
            this.fetchFile();
        },
    },
    methods: {
        resetFileState() {
            this.file = {
                name: '',
                original_name: '',
                short_description: '',
                category_id: null,
                employee_permissions: [],
                customer_permissions: [],
                can_write: false,
                can_manage_permissions: false,
                can_delete: false,
            };
            this.employeeMap = {};
            this.customerMap = {};
            this.replacementFile = null;
            this.replacementFileName = '';
            if (this.$refs.replaceInput) {
                this.$refs.replaceInput.value = null;
            }
        },
        fetchOptions() {
            const self = this;
            axios.get('api/workspace/file-manager/options').then(function (response) {
                self.categories = response.data.categories || [];
                self.employees = response.data.employees || [];
                self.guests = response.data.guests || [];
                self.customers = response.data.customers || [];
                self.employees.forEach((employee) => {
                    self.ensurePermissionState(self.employeeMap, employee.id);
                });
                self.guests.forEach((guest) => {
                    self.ensurePermissionState(self.employeeMap, guest.id);
                });
                self.customers.forEach((customer) => {
                    self.ensurePermissionState(self.customerMap, customer.id);
                });
                self.applyPermissionState();
            });
        },
        fetchFile() {
            const self = this;
            self.loading = true;
            const uuid = self.$route.params.uuid;
            axios.get('api/workspace/file-manager/' + self.$route.params.uuid).then(function (response) {
                if (uuid !== self.$route.params.uuid) {
                    return;
                }
                self.file = response.data;
                self.loading = false;
                self.applyPermissionState();
            }).catch(function () {
                self.loading = false;
            });
        },
        applyPermissionState() {
            const self = this;
            if (!self.file || !self.file.employee_permissions) {
                return;
            }

            (self.file.employee_permissions || []).forEach((permission) => {
                const state = self.ensurePermissionState(self.employeeMap, permission.id);
                state.read = true;
                state.write = !!permission.can_write;
            });

            (self.file.customer_permissions || []).forEach((permission) => {
                const state = self.ensurePermissionState(self.customerMap, permission.id);
                state.read = true;
                state.write = !!permission.can_write;
            });

            self.enforceGuestReadOnlyPermissions();
        },
        ensurePermissionState(map, id) {
            const key = Number(id);
            if (!map[key]) {
                map[key] = {read: false, write: false};
            }

            return map[key];
        },
        handlePermissionReadChange(state) {
            if (!state) {
                return;
            }

            if (!state.read) {
                state.write = false;
            }
        },
        handlePermissionWriteChange(state) {
            if (!state) {
                return;
            }

            if (state.write) {
                state.read = true;
            }
        },
        handleGuestPermissionReadChange(state) {
            if (!state) {
                return;
            }

            state.write = false;
        },
        enforceGuestReadOnlyPermissions() {
            this.guestEmployees.forEach((guest) => {
                const state = this.ensurePermissionState(this.employeeMap, guest.id);
                state.write = false;
            });
        },
        isGuestEmployee(id) {
            const guestId = Number(id);
            return this.guestEmployees.some((guest) => Number(guest.id) === guestId);
        },
        getEmployeePermissionState(id) {
            return this.ensurePermissionState(this.employeeMap, id);
        },
        getGuestPermissionState(id) {
            const state = this.ensurePermissionState(this.employeeMap, id);
            state.write = false;
            return state;
        },
        getCustomerPermissionState(id) {
            return this.ensurePermissionState(this.customerMap, id);
        },
        buildEmployeePermissions() {
            return Object.keys(this.employeeMap).reduce((carry, key) => {
                const id = Number(key);
                const permission = this.employeeMap[id];
                if (permission.read) {
                    carry.push({id: id, can_write: this.isGuestEmployee(id) ? false : !!permission.write});
                }
                return carry;
            }, []);
        },
        buildCustomerPermissions() {
            return Object.keys(this.customerMap).reduce((carry, key) => {
                const id = Number(key);
                const permission = this.customerMap[id];
                if (permission.read) {
                    carry.push({id: id, can_write: !!permission.write});
                }
                return carry;
            }, []);
        },
        saveFile() {
            const self = this;
            if (!self.canEditFileDetails) {
                return;
            }

            self.loading = true;

            const payload = new FormData();
            payload.append('_method', 'PUT');
            payload.append('name', self.file.name || '');
            payload.append('short_description', self.file.short_description || '');
            if (self.canManageCategories) {
                payload.append('category_id', self.file.category_id ? Number(self.file.category_id) : '');
            }

            if (self.canManageFilePermissions) {
                self.buildEmployeePermissions().forEach(function (permission, index) {
                    payload.append(`employee_permissions[${index}][id]`, permission.id);
                    payload.append(`employee_permissions[${index}][can_write]`, permission.can_write ? 1 : 0);
                });

                self.buildCustomerPermissions().forEach(function (permission, index) {
                    payload.append(`customer_permissions[${index}][id]`, permission.id);
                    payload.append(`customer_permissions[${index}][can_write]`, permission.can_write ? 1 : 0);
                });
            }

            if (self.replacementFile) {
                payload.append('file', self.replacementFile);
            }

            axios.post('api/workspace/file-manager/' + self.$route.params.uuid, payload, {
                headers: {'Content-Type': 'multipart/form-data'}
            }).then(function (response) {
                self.file = response.data.file;
                self.replacementFile = null;
                self.replacementFileName = '';
                if (self.$refs.replaceInput) {
                    self.$refs.replaceInput.value = null;
                }
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.loading = false;
            });
        },
        selectReplaceFile() {
            if (!this.canEditFileDetails) {
                return;
            }
            this.$refs.replaceInput.click();
        },
        onReplaceFileSelected(event) {
            if (!event.target.files || !event.target.files.length) {
                this.replacementFile = null;
                this.replacementFileName = '';
                return;
            }
            this.replacementFile = event.target.files[0];
            this.replacementFileName = this.replacementFile.name;
        },
        getUserAvatar(user) {
            if (!user) {
                return '';
            }
            if (user.avatar && user.avatar !== 'gravatar') {
                return user.avatar;
            }
            if (user.gravatar) {
                return user.gravatar;
            }
            return '';
        },
        deleteFile() {
            const self = this;
            if (!self.canDeleteFile) {
                return;
            }
            self.loading = true;
            axios.delete('api/workspace/file-manager/' + self.$route.params.uuid).then(function () {
                self.loading = false;
                self.$router.push(self.fileManagerListPath);
            }).catch(function () {
                self.loading = false;
                self.deleteModal = false;
            });
        },
    }
}
</script>
